import { Button } from "@/shared/ui/button";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { formatTimestamp } from "@/shared/lib/date-utils";
import { labelOr } from "@/shared/lib/label";
import type { ExternalTradeEvent } from "@/features/portfolio/api/external-trade";

export type LogsStatus = "loading" | "loaded" | "failed";

export interface LogsState {
  status: LogsStatus;
  events: ExternalTradeEvent[];
}

interface TransactionLogsProps {
  state: LogsState | undefined;
  onRetry: () => void;
}

export const TransactionLogs = ({ state, onRetry }: TransactionLogsProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();

  if (!state || state.status === "loading") {
    return (
      <div className="mt-4 space-y-2" data-testid="transaction-logs">
        <div className="skeleton-shimmer h-4 w-2/3 rounded bg-muted" />
        <div className="skeleton-shimmer h-4 w-1/2 rounded bg-muted" />
      </div>
    );
  }

  if (state.status === "failed") {
    return (
      <div className="mt-4 space-y-2" data-testid="transaction-logs">
        <p className="text-sm text-destructive" data-testid="transaction-logs-failed">
          {t("investorDetail.logsFailed")}
        </p>
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="transaction-logs-retry">
          {t("investorDetail.retry")}
        </Button>
      </div>
    );
  }

  if (state.events.length === 0) {
    return (
      <p
        className="mt-4 text-sm text-muted-foreground"
        data-testid="transaction-logs-empty"
      >
        {t("investorDetail.logsEmpty")}
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-2" data-testid="transaction-logs">
      {state.events.map((event) => (
        <div
          key={event.id}
          className="flex flex-col gap-1 rounded-lg bg-muted/40 p-3 text-sm sm:flex-row sm:gap-3"
          data-testid="transaction-log-row"
        >
          <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
            {formatTimestamp(event.created, dateFormat)}
          </span>
          <div className="flex-1">
            <span>{labelOr(t, `tradeEvent.${event.type}`, event.type)}</span>
            {event.reasons?.map((reason) => (
              <p key={reason.code} className="text-destructive">
                {labelOr(t, `tradeReason.${reason.code}`, reason.message || reason.code)}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
